import React, { useCallback, useState, useEffect } from 'react';
import { SectionBase } from './SectionBase';
import Selector, {
  MAX_WIDTH_FOR_SELECTOR,
  SelectorOptions,
} from 'shared/design-system/components/Selector';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { OtherTextField } from './ImpactSummary/SectionService';
import { GuidanceTextResponse } from './utils';

const parseValue = (
  value: string | (string | number)[] | undefined,
): (string | number)[] => {
  try {
    return typeof value === 'string'
      ? JSON.parse(value.replace(/'/g, '"'))
      : value;
  } catch (error) {
    return [];
  }
};

export interface Report {
  end_date: string;
  fully_allocated: boolean;
  id: number; // Report id
  name: string;
  start_date: string; // "2018-01-01"
  year: number;
}

export interface Program {
  id: number;
  name: string;
  period: string;
  reports?: Report[];
  valid?: boolean;
  year: number | '' | number[]; // report ids when its number[]
}

interface SectionCheckboxesProps {
  title: string;
  subTitle: string;
  helpText: string;
  questionId: number;
  guidanceText: GuidanceTextResponse;
  onChange: (questionId: number, value: (string | number)[]) => unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  options: Program[];
  value: string | (number | string)[] | undefined;
}

const getOtherText = (selected: (number | string)[]): string | undefined => {
  return selected.find(x => typeof x === 'string') as string | undefined;
};

const defaultGetLabel = (value: Program) => value.name;

export const SectionPrograms: React.FC<SectionCheckboxesProps> = ({
  title,
  subTitle,
  helpText,
  options,
  questionId,
  value,
  guidanceText,
  onChange,
  error,
}) => {
  const [selected, setSelected] = useState<(string | number)[]>(
    parseValue(value) || [],
  );
  const [other, setOther] = useState<string | undefined>(
    getOtherText(selected),
  );

  useEffect(() => {
    onChange(questionId, selected);
  }, [selected]);

  const handleChange = useCallback(
    (programs: Program) => {
      setSelected(s => {
        const newState = [...s];
        const index = newState.findIndex(x => x === programs.id);
        if (index === -1) {
          newState.push(programs.id);
        } else {
          newState.splice(index, 1);
        }
        return newState;
      });
    },
    [questionId, onChange, setSelected],
  );

  const handleBlur = useCallback(() => {
    setSelected(s => {
      const newState = [...s];
      const index = newState.findIndex(x => typeof x === 'string');
      if (index === -1) {
        if (!other) return s;
        newState.push(other);
      } else {
        newState.splice(index, 1);
        other && newState.push(other);
      }
      return newState;
    });
  }, [other, onChange, setSelected]);

  const handleWrite = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setOther(value);
    },
    [setOther],
  );

  const handleOtherClick = useCallback(() => {
    setOther(o => (o === undefined ? '' : undefined));
    setSelected(s => {
      const newState = [...s];
      const index = s.findIndex(x => typeof x === 'string');
      if (index !== -1) {
        newState.splice(index, 1);
        return newState;
      }
      return s;
    });
  }, [setOther, onChange]);

  return (
    <SectionBase
      title={title}
      subTitle={subTitle}
      helpText={helpText}
      error={error}
      questionId={questionId}
      guidanceText={guidanceText}
    >
      <SelectorOptions
        variant="checkbox"
        padding={SPACERS.tiny}
        options={options}
        getLabel={defaultGetLabel}
        getSelected={val => selected.findIndex(x => val.id === x) !== -1}
        onClick={handleChange}
      />
      <Selector
        variant="checkbox"
        label="Other"
        selected={other !== undefined}
        maxWidth={MAX_WIDTH_FOR_SELECTOR}
        padding={SPACERS.tiny}
        margin={`${SPACERS.tiny} auto`}
        onClick={handleOtherClick}
      />
      {other !== undefined && (
        <OtherTextField
          value={other || ''}
          placeholder="Please specify"
          onChange={handleWrite}
          onBlur={handleBlur}
          autoFocus={true}
        />
      )}
    </SectionBase>
  );
};
